<template>
  <b-card class="revenue-trend-chart" v-if="hasRight(user, 'dashboard', 'view')" no-body>
    <template #header>
      <h6 class="mb-0">Monthly Revenue Collection Trend</h6>
    </template>
    <b-card-body>
      <apexchart type="line" height="350" :options="chartOptions" :series="safeSeries"></apexchart>
    </b-card-body>
  </b-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { BCard, BCardBody } from 'bootstrap-vue';
import { hasRight } from '@/utils/auth';
import { dashboardGet } from './dashboardApi';

export default {
  name: 'RevenueTrendChart',
  components: {
    BCard,
    BCardBody,
    apexchart: VueApexCharts,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    dashboardData: { type: Object, default: null },
    useAggregate: { type: Boolean, default: false },
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
  },
  data() {
    return {
      chartOptions: {
        chart: {
          type: 'line',
          height: 350,
          toolbar: {
            show: false,
          },
        },
        stroke: {
          curve: 'smooth',
        },
        xaxis: {
          categories: [], // Months will go here
        },
        yaxis: {
          title: {
            text: 'Revenue',
          },
          labels: {
            formatter: function (val) {
              return `$${val.toFixed(2)}`;
            },
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return `$${val.toFixed(2)}`;
            },
          },
        },
      },
      series: [{
        name: 'Revenue',
        data: [],
      }],
    };
  },
  computed: {
    safeSeries() {
      const s = this.series || [];
      const mapped = s.map((item) => ({ name: item.name || '', data: Array.isArray(item.data) ? item.data : [] }));
      return mapped.length ? mapped : [{ name: 'Revenue', data: [] }];
    },
  },
  watch: {
    filters: {
      deep: true,
      immediate: true,
      handler() {
        if (!hasRight(this.user, 'dashboard', 'view')) return;
        if (this.useAggregate) {
          if (this.dashboardData && this.dashboardData.revenue_trend != null) this.processChartData(this.dashboardData.revenue_trend);
          return;
        }
        if (this.dashboardData && this.dashboardData.revenue_trend != null) { this.processChartData(this.dashboardData.revenue_trend); return; }
        this.fetchRevenueTrendData();
      },
    },
    user: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (hasRight(newVal, 'dashboard', 'view')) {
          if (this.useAggregate) {
            if (this.dashboardData && this.dashboardData.revenue_trend != null) this.processChartData(this.dashboardData.revenue_trend);
            return;
          }
          this.fetchRevenueTrendData();
        } else {
          this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
          this.series = [{ name: 'Revenue', data: [] }];
        }
      },
    },
  },
  methods: {
    hasRight, // Make the helper available in the template
    async fetchRevenueTrendData() {
      if (!hasRight(this.user, 'dashboard', 'view')) return;
      if (this.useAggregate || (this.dashboardData && this.dashboardData.revenue_trend != null)) {
        if (this.dashboardData && this.dashboardData.revenue_trend != null) this.processChartData(this.dashboardData.revenue_trend);
        return;
      }
      try {
        const response = await dashboardGet(this.baseApi, this.accessToken, '/dashboard/crm/revenue-trend', this.filters);
        const raw = response.data && response.data.data;
        this.processChartData(Array.isArray(raw) ? raw : []);
      } catch (error) {
        console.error('Error fetching monthly revenue trend data:', error);
        // Handle error
      }
    },
    processChartData(data) {
      if (!data || !Array.isArray(data) || data.length === 0) {
        this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
        this.series = [{ name: 'Revenue', data: [] }];
        return;
      }

      const categories = data.map(item => item.month);
      const seriesData = data.map(item => item.revenue);

      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          categories: categories,
        },
      };

      this.series = [{
        name: 'Revenue',
        data: seriesData,
      }];
    },
  },
};
</script>

<style scoped>
.revenue-trend-chart {
  margin-top: 20px;
}
</style>